import { useCallback, useMemo } from 'react';
import { ExternalLink, Copy } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../components/ui/tabs';
import { toast } from '../../../../components/ui/toaster';
import { cn } from '../../../../lib/utils';
import { writeClipboardText } from '../../../../lib/clipboard';
import { getExternalToolHelpBasics, type ExternalToolHelpTopic } from '../externalToolHelp';

type ExternalToolHowToFixDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  topic: ExternalToolHelpTopic;
  missingTools?: string[];
};

function CommandBlock({
  title,
  commands,
  className,
}: {
  title: string;
  commands: string[];
  className?: string;
}) {
  const text = commands.join('\n');

  const handleCopy = useCallback(async () => {
    const ok = await writeClipboardText(text);
    if (ok) {
      toast.success({ title: 'Copied to clipboard' });
    } else {
      toast.error({ title: 'Copy failed', description: 'Could not copy to clipboard' });
    }
  }, [text]);

  return (
    <div className={cn('w-full min-w-0 rounded-md border bg-muted/40 overflow-hidden', className)}>
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b">
        <span className="text-xs font-medium text-muted-foreground">{title}</span>
        <Button variant="ghost" size="sm" className="h-7 px-2" onClick={handleCopy}>
          <Copy className="h-3.5 w-3.5 mr-1.5" />
          Copy
        </Button>
      </div>
      <pre className="p-3 text-xs max-h-56 overflow-auto">
        <code>{text}</code>
      </pre>
    </div>
  );
}

export function ExternalToolHowToFixDialog({
  open,
  onOpenChange,
  topic,
  missingTools,
}: ExternalToolHowToFixDialogProps) {
  const help = useMemo(() => getExternalToolHelpBasics(topic), [topic]);
  const missingLabel = missingTools?.length ? missingTools.join(', ') : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[760px] max-h-[85vh] overflow-hidden grid-rows-[auto_1fr_auto]">
        <DialogHeader>
          <DialogTitle>{help.title}</DialogTitle>
          <DialogDescription>
            {missingLabel ? (
              <>
                Missing: <span className="font-medium text-foreground">{missingLabel}</span>
                <span className="text-muted-foreground"> • </span>
              </>
            ) : null}
            Required tools: {help.requiredBinaries.join(', ')}.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 overflow-y-auto space-y-4 pr-2">
          <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
            {help.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>

          <div className="flex flex-wrap gap-2">
            {help.links.map((link) => (
              <a
                key={link.url}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-primary underline-offset-4 hover:underline"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                {link.label}
              </a>
            ))}
          </div>

          <Tabs defaultValue="macos">
            <TabsList>
              <TabsTrigger value="macos">macOS</TabsTrigger>
              <TabsTrigger value="windows">Windows</TabsTrigger>
              <TabsTrigger value="linux">Linux</TabsTrigger>
            </TabsList>

            <TabsContent value="macos" className="mt-3 space-y-3">
              <CommandBlock title="Install (Homebrew)" commands={help.installCommands.macos} />
              <CommandBlock title="Verify" commands={help.verifyCommands} />
            </TabsContent>

            <TabsContent value="windows" className="mt-3 space-y-3">
              <CommandBlock title="Install (notes)" commands={help.installCommands.windows} />
              <CommandBlock title="Verify (PowerShell / cmd)" commands={help.verifyCommands} />
            </TabsContent>

            <TabsContent value="linux" className="mt-3 space-y-3">
              <CommandBlock title="Install (Debian/Ubuntu)" commands={help.installCommands.linux} />
              <CommandBlock title="Verify" commands={help.verifyCommands} />
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter>
          <div className="flex-1 text-xs text-muted-foreground">
            {help.afterInstallSteps.join(' ')}
          </div>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
